import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';

import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import CheckboxInputField from '@/components/inputs/CheckboxInputField';
import TextInputField from '@/components/inputs/TextInputField';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { GeneralData, generalResolver } from '@/lib/validations/NpoOnboardingSchema';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { toPointString } from '@/utils/parsePointString';

import GoogleAutoComplete from '../inputs/GoogleAutocomplete';
import ImageDropZonePickerField from '../inputs/ImageDropZonePickerField';
import ImagePickerField from '../inputs/ImagePickerField';

interface NpoGeneralFormProps {
  nonProfitOrg: NonProfitOrg | undefined;
  isLoading: boolean;
  fileProgress: number | undefined;
  showBackButton?: boolean;
  submitLabel?: string;
  onSubmitTapped: (data: GeneralData) => void;
}

const NpoGeneralForm = ({
  nonProfitOrg,
  isLoading,
  fileProgress,
  showBackButton = true,
  submitLabel = i18n.t('mix.next'),
  onSubmitTapped
}: NpoGeneralFormProps) => {
  const { state: authState } = useAuth();

  const [initialAddress, setInitialAddress] = useState<string | undefined>(undefined);

  const initialData: GeneralData = {
    name: nonProfitOrg?.name ?? '',
    description: nonProfitOrg?.description ?? '',
    email: nonProfitOrg?.email ?? '',
    address: nonProfitOrg?.location?.coordinates ? nonProfitOrg?.address : undefined,
    location: nonProfitOrg?.location?.coordinates ? toPointString(nonProfitOrg?.location?.coordinates) : undefined,
    showMap: authState.signUpData?.loginEmail?.type === 'CHURCH' ? null : nonProfitOrg?.showMap,
    totalEmployees: nonProfitOrg?.totalEmployees ?? undefined,
    totalVolunteers: nonProfitOrg?.totalVolunteers ?? undefined,
    thumbnail: typeof nonProfitOrg?.thumbnail === 'string' ? safeJsonParseArray(nonProfitOrg?.thumbnail) : []
  } as GeneralData;

  useEffect(() => {
    if (initialData.address) setInitialAddress(initialData.address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData.address]);

  const {
    control,
    handleSubmit,
    setValue,
    resetField,
    setError,
    setFocus,
    trigger,
    formState: { isValid, submitCount, errors }
  } = useForm<GeneralData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: generalResolver as any
  });

  const onSubmit: SubmitHandler<GeneralData> = async (data) => {
    onSubmitTapped(data);
  };

  return (
    <FormWrapper>
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.general.general_section')}
          description={i18n.t('sign_in.nonProfit.general.general_section_description')}
        />

        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.general.name_section')}
          description={i18n.t('sign_in.nonProfit.general.name_section_description')}>
          <TextInputField
            control={control}
            name="name"
            placeholder={i18n.t('sign_in.nonProfit.general.name_placeholder')}
            onSubmitEditing={() => setFocus('description')}
            returnKeyLabel="next"
            returnKeyType="next"
          />
        </OnboardingSection>

        <Divider />

        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.general.description_section')}
          description={i18n.t('sign_in.nonProfit.general.description_section_description')}>
          <TextInputField
            control={control}
            name="description"
            multiline
            minHeight={120}
            maxHeight={120}
            placeholder={i18n.t('sign_in.nonProfit.general.description_placeholder')}
            onSubmitEditing={() => setFocus('email')}
            returnKeyLabel="next"
            returnKeyType="next"
          />
        </OnboardingSection>

        <Divider />

        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.general.email_section')}
          description={i18n.t('sign_in.nonProfit.general.email_section_description')}>
          <TextInputField
            control={control}
            name="email"
            type="email-address"
            placeholder={i18n.t('sign_in.nonProfit.general.email_placeholder')}
            onSubmitEditing={() => setFocus('address')}
            returnKeyLabel="next"
            returnKeyType="next"
          />
        </OnboardingSection>

        <Divider />

        <View style={styles.autoCompleteSection}>
          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.general.address_section')}
            description={i18n.t('sign_in.nonProfit.general.address_section_description')}>
            <View style={styles.autoComplete}>
              <GoogleAutoComplete
                initialValue={initialAddress}
                type="address"
                predictionsScrollEnabled={false}
                error={errors.address}
                onChange={(result) => {
                  if (result?.lat === 0 && result.lng === 0) return;
                  if (result) {
                    setValue('location', `${result.lat},${result.lng}`);
                    setValue('address', result.formattedAddress);
                    trigger();
                  } else {
                    resetField('location');
                    resetField('address');
                    setError('address', {
                      type: 'required',
                      message: i18n.t('sign_up.nonProfit.wizard.errors.general.address')
                    });
                  }
                }}
              />
            </View>

            {authState.signUpData?.loginEmail?.type === 'CHARITIES' && (
              <CheckboxInputField
                control={control}
                name="showMap"
                label={i18n.t('sign_in.nonProfit.general.show_map_label')}
              />
            )}
          </OnboardingSection>
        </View>

        <Divider />

        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.general.total_employees_section')}
          description={i18n.t('sign_in.nonProfit.general.total_employees_section_description')}>
          <TextInputField
            type="numeric"
            control={control}
            name="totalEmployees"
            placeholder={i18n.t('sign_in.nonProfit.general.total_employees_placeholder')}
            onSubmitEditing={() => setFocus('totalVolunteers')}
            returnKeyType="done"
          />
        </OnboardingSection>

        <Divider />

        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.general.total_volunteers_section')}
          description={i18n.t('sign_in.nonProfit.general.total_volunteers_section_description')}>
          <TextInputField
            type="numeric"
            control={control}
            name="totalVolunteers"
            placeholder={i18n.t('sign_in.nonProfit.general.total_volunteers_placeholder')}
            returnKeyLabel="done"
            returnKeyType="done"
          />
        </OnboardingSection>

        <Divider />

        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.general.thumbnail_section')}
          description={i18n.t('sign_in.nonProfit.general.thumbnail_section_description')}>
          {Platform.OS === 'web' ? (
            <ImageDropZonePickerField control={control} name="thumbnail" progress={fileProgress} />
          ) : (
            <ImagePickerField control={control} name="thumbnail" progress={fileProgress} />
          )}
        </OnboardingSection>

        <Text style={styles.footer}>{i18n.t('sign_in.nonProfit.general.optimal_image_size')}</Text>

        <View style={styles.nextBackContainer}>
          {showBackButton && router.canGoBack() && (
            <BrandedButton disabled={isLoading} style={styles.flexOne} onPress={router.back}>
              {capitalize(i18n.t('mix.back'))}
            </BrandedButton>
          )}
          <BrandedButton
            style={styles.flexOne}
            loading={isLoading}
            disabled={!isValid && submitCount > 0}
            onPress={handleSubmit(onSubmit)}>
            {submitLabel}
          </BrandedButton>
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    gap: 25
  },

  footer: {
    flex: 1,
    justifyContent: 'center',
    textAlign: 'center'
  },

  flexOne: {
    flex: 1
  },

  autoComplete: {
    zIndex: 1
  },

  autoCompleteSection: {
    zIndex: 1
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  }
});

export default NpoGeneralForm;
